import { useState } from 'react';
import PopOverModal from '../../components/popover';
import {
  Box,
  HStack,
  Pressable,
  Text,
  Tooltip,
  VStack,
  Avatar,
  FormControl,
  Input,
  Select,
  CheckIcon,
  useMediaQuery
} from 'native-base';
import Svg from 'react-native-svg';
import { profileAvatarSVG } from '../../utils/svg';
import { deleteCardsIconSVG, editIconDetailsPage } from '../../utils/svg';
import { COLORS } from '../../utils/colors';
import { MOBILE_VIEW_WIDTH } from '../../utils/screens';
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from '../../utils/fonts';
import CommonBadge from '../../components/badge';
import DividerLine from '../../components/divider';
import { uploadIconSVG } from '../../utils/svg';
import { hasPermission, statusMap } from '../../utils/permission';
import DocumentList from '../../components/menuItem';
import CustomModal from '../../components/modal';
import MobileModelSheet from '../../components/actionSheet';
import CustomButton from '../../components/button';
import { Toaster } from '../../components/toaster';
import { LoaderAnimation } from '../../components/spinner';
import { editAttendeeAPI } from '../../api/apis/workflow';
import config from '../../utils/config';

const PeopleDetails = ({
  people,
  setUploadActiveButton = () => { },
  openModal = () => { },
  handleUploadDoc = () => { },
  status = '',
  selected,
  total,
  eventsDetail,
  getEventDetails
}) => {
  const toaster = Toaster();
  const [MobileWindow] = useMediaQuery({
    maxWidth: MOBILE_VIEW_WIDTH
  });
  const [isOpen, setIsOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    display_name: '',
    category: '',
    phone_code: '',
    phone_number: '',
    company_name: ''
  });
  const [customCategory, setCustomCategory] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const categoryOptions = [
    { label: 'Organizer', value: 'organizer' },
    { label: 'Participant', value: 'participant' },
    { label: 'Invitee', value: 'invitee' },
    { label: 'Speaker', value: 'speaker' },
    { label: 'Volunteer', value: 'volunteer' },
    { label: 'Other', value: 'other' }
  ];

  const onOpenPress = () => {
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const capitalizeFirst = (str) =>
    str?.charAt(0)?.toUpperCase() + str?.slice(1);

  const formatPhoneNumber = (phoneCode, phoneNumber) => {
    if (!phoneCode || !phoneNumber) return '';
    const code = phoneCode.startsWith('+') ? phoneCode : `+${phoneCode}`;
    return `${code} ${phoneNumber}`;
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleEditClick = (personData) => {
    scrollToTop();
    // Format phone code to ensure it has + if it exists
    let formattedPhoneCode = personData?.phone_code || '';
    if (formattedPhoneCode && !formattedPhoneCode.startsWith('+')) {
      formattedPhoneCode = '+' + formattedPhoneCode;
    }
    
    // Normalize category: convert 'organiser' to 'organizer'
    let normalizedCategory = personData?.category?.toLowerCase() || '';
    if (normalizedCategory === 'organiser') {
      normalizedCategory = 'organizer';
    }
    
    setEditFormData({
      display_name: personData?.display_name || '',
      category: normalizedCategory,
      phone_code: formattedPhoneCode,
      phone_number: personData?.phone_number || '',
      company_name: personData?.company_name || ''
    });
    // Check if current category is not in predefined options, set as custom
    const predefinedCategories = ['organizer', 'organiser', 'participant', 'invitee', 'speaker', 'volunteer'];
    if (personData?.category && !predefinedCategories.includes(personData?.category.toLowerCase())) {
      setCustomCategory(personData?.category);
      setEditFormData(prev => ({ ...prev, category: 'other' }));
    } else {
      setCustomCategory('');
    }
    setShowEditModal(true);
  };

  const handleDeleteClick = (personData) => {
    scrollToTop();
    const category = people.category?.toLowerCase();
    if ((category === 'organizer' || category === 'organiser') && isCoordinatorEmail(people.email)) {
      toaster('Cannot delete Organiser', 'error', 'error');
      return;
    }
    setShowDeleteModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      setIsEditing(true);
      LoaderAnimation.show();

      // Validate required fields
      if (!editFormData.display_name || editFormData.display_name.trim() === '') {
        toaster('Display name is required', 'error', 'error');
        setIsEditing(false);
        LoaderAnimation.hide();
        return;
      }

      if (!editFormData.company_name || editFormData.company_name.trim() === '') {
        toaster('Company name is required', 'error', 'error');
        setIsEditing(false);
        LoaderAnimation.hide();
        return;
      }

      // Validate custom category when Other is selected
      if (editFormData.category === 'other' && (!customCategory || customCategory.trim() === '')) {
        toaster('Please enter a category', 'error', 'error');
        setIsEditing(false);
        LoaderAnimation.hide();
        return;
      }

      // Validate phone number fields (basic validation only)
      if (editFormData.phone_number && (editFormData.phone_number.length < 10 || editFormData.phone_number.length > 15)) {
        toaster('Phone number must be between 10 and 15 digits', 'error', 'error');
        setIsEditing(false);
        LoaderAnimation.hide();
        return;
      }

      // Find the index of the attendee being edited
      const attendeeIndex = eventsDetail.employee.findIndex(
        (emp) => emp.email === people.email // Use email as identifier since _id might not be consistent
      );

      if (attendeeIndex === -1) {
        toaster('Attendee not found', 'error', 'error');
        return;
      }

      // Create updated employee array with all existing employees
      const updatedEmployeeArray = [...eventsDetail.employee];

      // Determine the final category value
      const finalCategory = editFormData.category === 'other' ? customCategory : editFormData.category;

      // Update the specific employee with new data
      updatedEmployeeArray[attendeeIndex] = {
        ...updatedEmployeeArray[attendeeIndex], // Keep existing data
        ...(editFormData.display_name && {
          display_name: editFormData.display_name
        }),
        ...(finalCategory && { category: finalCategory }),
        phone_code: editFormData.phone_code || '',
        phone_number: editFormData.phone_number || '',
        company_name: editFormData.company_name || ''
      };

      // Prepare the payload with entire employee array and all other existing data
      const payload = {
        ...eventsDetail,
        workflow_instance_id: eventsDetail._id,
        employee: updatedEmployeeArray // Send entire updated array
      };
      delete payload._id;
      delete payload.quiz_title;

      // Call the API to update the attendee
      const response = await editAttendeeAPI(
        eventsDetail.workflow_id,
        eventsDetail.current_step,
        eventsDetail._id,
        payload
      );

      if (response && response.error) {
        toaster(
          response.message || 'Failed to update attendee',
          'error',
          'error'
        );
      } else {
        toaster(
          response.data.data.message || 'Attendee updated successfully',
          'success',
          'success'
        );
        setShowEditModal(false);
        if (typeof getEventDetails === 'function') getEventDetails();
      }
    } catch (error) {
      toaster('Error updating attendee', 'error', 'error');
    } finally {
      setIsEditing(false);
      LoaderAnimation.hide();
    }
  };

  const handleDeletePerson = async () => {
    try {
      setIsDeleting(true);
      LoaderAnimation.show();

      // Filter out the employee to be deleted from the array
      const updatedEmployeeArray = eventsDetail.employee.filter(
        (emp) => emp.email !== people.email // Use email as identifier
      );

      // Prepare the payload with updated employee array (without the deleted employee)
      const payload = {
        ...eventsDetail,
        workflow_instance_id: eventsDetail._id,
        employee: updatedEmployeeArray // Send array without deleted employee
      };
      delete payload._id;
      delete payload.quiz_title;

      // Call the API to update the employee list
      const response = await editAttendeeAPI(
        eventsDetail.workflow_id,
        eventsDetail.current_step,
        eventsDetail._id,
        payload
      );

      if (response && response.error) {
        toaster(
          response.message || 'Failed to delete person',
          'error',
          'error'
        );
      } else {
        toaster(
          response.data.data.message || 'Person deleted successfully',
          'success',
          'success'
        );
        setShowDeleteModal(false);
        if (typeof getEventDetails === 'function') getEventDetails();
      }
    } catch (error) {
      toaster('Error deleting person', 'error', 'error');
    } finally {
      setIsDeleting(false);
      LoaderAnimation.hide();
    }
  };

  // Check if the person is an organizer and their email matches the coordinator email
  const isCoordinatorEmail = (email) => {
    if (!eventsDetail?.contact_details?.email) return false;
    return eventsDetail.contact_details.email === email;
  };

  const category = people.category?.toLowerCase();
  const shouldHideEditDeleteIcons = (category === 'organizer' || category === 'organiser') && isCoordinatorEmail(people.email);

  const popOverBody = () => {
    return (
      <Text
        onPress={() => {
          setUploadActiveButton('upload_docs');
          onClose();
          openModal();
        }}
        color={COLORS.TOGGLE_ACTIVE_BUTTON_COLOR}
        fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE}
        fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
      >
        Upload Document
      </Text>
    );
  };

  const getEditModalHeader = () => {
    return (
      <Text
        fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
        fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}
        color={COLORS.ACCORDION_TITLE_COLOR}
      >
        Edit Attendee Details
      </Text>
    );
  };

  // Edit Modal Body
  const getEditModalBody = () => {
    return (
      <VStack
        space={2}
        alignItems='center'
        w='100%'
      >
        {renderProfileImageForEditModal()}
        <FormControl isRequired>
          <FormControl.Label>
            <Text
              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
              fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
              color={COLORS.ATTACHMENTS_TEXT_COLOR}
            >
              Display Name
            </Text>
          </FormControl.Label>
          <Input
            fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
            value={editFormData.display_name}
            onChangeText={(text) =>
              setEditFormData((prev) => ({ ...prev, display_name: text }))
            }
            placeholder='Enter display name'
            borderColor={COLORS.SOLID_DIVIDER_COLOR}
            _focus={{
              borderColor: COLORS.PRIMARY_COLOR,
              bg: 'white'
            }}
          />
        </FormControl>

        <FormControl>
          <FormControl.Label>
            <Text
              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
              fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
              color={COLORS.ATTACHMENTS_TEXT_COLOR}
            >
              About
            </Text>
          </FormControl.Label>
          <Box
            bg='gray.100'
            borderRadius='md'
            borderWidth='1'
            borderColor={COLORS.SOLID_DIVIDER_COLOR}
            px={3}
            py={2}
          >
            <Text
              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
              color={COLORS.ATTACHMENTS_TEXT_COLOR}
            >
              {people?.about || 'N/A'}
            </Text>
          </Box>
        </FormControl>

        <FormControl>
          <FormControl.Label>
            <Text
              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
              fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
              color={COLORS.ATTACHMENTS_TEXT_COLOR}
            >
              Email
            </Text>
          </FormControl.Label>
          <Box
            bg='gray.100'
            borderRadius='md'
            borderWidth='1'
            borderColor={COLORS.SOLID_DIVIDER_COLOR}
            px={3}
            py={2}
          >
            <Text
              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
              color={COLORS.ATTACHMENTS_TEXT_COLOR}
            >
              {people?.email || 'N/A'}
            </Text>
          </Box>
        </FormControl>

        <HStack space={4} w='100%'>
          <FormControl flex={1}>
            <FormControl.Label>
              <Text
                fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                color={COLORS.ATTACHMENTS_TEXT_COLOR}
              >
                Phone Code
              </Text>
            </FormControl.Label>
            <Input
              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
              value={editFormData.phone_code}
              onChangeText={(text) => {
                // Remove any non-digit characters except +
                let cleaned = text.replace(/[^\d+]/g, '');
                // If it doesn't start with +, add it
                if (cleaned && !cleaned.startsWith('+')) {
                  cleaned = '+' + cleaned;
                }
                setEditFormData((prev) => ({ ...prev, phone_code: cleaned }));
              }}
              placeholder='+91'
              borderColor={COLORS.SOLID_DIVIDER_COLOR}
              _focus={{
                borderColor: COLORS.PRIMARY_COLOR,
                bg: 'white'
              }}
              keyboardType='phone-pad'
              maxLength={4}
            />
          </FormControl>

          <FormControl flex={2}>
            <FormControl.Label>
              <Text
                fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                color={COLORS.ATTACHMENTS_TEXT_COLOR}
              >
                Phone Number
              </Text>
            </FormControl.Label>
            <Input
              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
              value={editFormData.phone_number}
              onChangeText={(text) => {
                // Remove any non-digit characters
                const cleaned = text.replace(/\D/g, '');
                setEditFormData((prev) => ({ ...prev, phone_number: cleaned }));
              }}
              placeholder='9876543210'
              borderColor={COLORS.SOLID_DIVIDER_COLOR}
              _focus={{
                borderColor: COLORS.PRIMARY_COLOR,
                bg: 'white'
              }}
              keyboardType='phone-pad'
              maxLength={15}
            />
          </FormControl>
        </HStack>

        <FormControl isRequired>
          <FormControl.Label>
            <Text
              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
              fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
              color={COLORS.ATTACHMENTS_TEXT_COLOR}
            >
              Company Name
            </Text>
          </FormControl.Label>
          <Input
            fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
            value={editFormData.company_name}
            onChangeText={(text) =>
              setEditFormData((prev) => ({ ...prev, company_name: text }))
            }
            placeholder='Enter company name'
            borderColor={COLORS.SOLID_DIVIDER_COLOR}
            _focus={{
              borderColor: COLORS.PRIMARY_COLOR,
              bg: 'white'
            }}
          />
        </FormControl>

        <FormControl isRequired>
          <FormControl.Label>
            <Text
              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
              fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
              color={COLORS.ATTACHMENTS_TEXT_COLOR}
            >
              Category
            </Text>
          </FormControl.Label>
          <Select
            fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
            selectedValue={editFormData.category}
            minWidth='200'
            accessibilityLabel='Choose Category'
            placeholder='Select category'
            _selectedItem={{
              bg: COLORS.PRIMARY_COLOR,
              endIcon: <CheckIcon size='5' />
            }}
            mt={1}
            onValueChange={(itemValue) => {
              setEditFormData((prev) => ({ ...prev, category: itemValue }));
              if (itemValue !== 'other') {
                setCustomCategory('');
              }
            }}
            borderColor={COLORS.SOLID_DIVIDER_COLOR}
            _focus={{
              borderColor: COLORS.PRIMARY_COLOR,
              bg: 'white'
            }}
          >
            {categoryOptions.map((option) => (
              <Select.Item
                key={option.value}
                label={option.label}
                value={option.value}
              />
            ))}
          </Select>
        </FormControl>

        {editFormData.category === 'other' && (
          <FormControl isRequired>
            <FormControl.Label>
              <Text
                fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                color={COLORS.ATTACHMENTS_TEXT_COLOR}
              >
                Enter Category 
              </Text>
            </FormControl.Label>
            <Input
              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
              value={customCategory}
              onChangeText={(text) => setCustomCategory(text)}
              placeholder='Enter category'
              borderColor={COLORS.SOLID_DIVIDER_COLOR}
              _focus={{
                borderColor: COLORS.PRIMARY_COLOR,
                bg: 'white'
              }}
            />
          </FormControl>
        )}
      </VStack>
    );
  };

  // Edit Modal Footer
  const getEditModalFooter = () => {
    return (
      <HStack
        justifyContent={'end'}
        width={'100%'}
        space={4}
      >
        <CustomButton
          onPress={() => setShowEditModal(false)}
          text='Cancel'
          color={COLORS.MODAL_BACK_BUTTON_COLOR}
          variant='outline'
        />
        <CustomButton
          onPress={handleSaveEdit}
          text={isEditing ? 'Saving...' : 'Update'}
          color={COLORS.WHITE_COLOR}
          backgroundColor={COLORS.PRIMARY_COLOR}
          isLoading={isEditing}
        />
      </HStack>
    );
  };

  // Delete Modal Header
  const getDeleteModalHeader = () => {
    return (
      <Text
        fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
        fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}
        color={COLORS.ACCORDION_TITLE_COLOR}
      >
        Delete Person
      </Text>
    );
  };

  // Delete Modal Body
  const getDeleteModalBody = () => {
    return (
      <Box
        justifyContent={'center'}
        alignItems={'center'}
      >
        <Text
          fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
          fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
          textAlign='center'
        >
          Are you sure you want to delete "
          {people.display_name || people.email || 'this person'}"?
        </Text>
      </Box>
    );
  };

  // Delete Modal Footer
  const getDeleteModalFooter = () => {
    return (
      <HStack
        justifyContent={'end'}
        width={'100%'}
        space={4}
      >
        <CustomButton
          onPress={() => setShowDeleteModal(false)}
          text='Cancel'
          color={COLORS.MODAL_BACK_BUTTON_COLOR}
          variant='outline'
        />
        <CustomButton
          onPress={handleDeletePerson}
          text={isDeleting ? 'Deleting...' : 'Delete'}
          color={COLORS.WHITE_COLOR}
          backgroundColor={COLORS.REJECT_TEXT_COLOR}
          isLoading={isDeleting}
        />
      </HStack>
    );
  };

  const renderProfileImage = () => {
    if (people?.image_url) {
      const imageUrl = people?.image_url?.includes('https')
        ? people?.image_url
        : `${config.bucketPath}${people?.image_url}`;

      return (
        <img
          src={imageUrl}
          alt='Profile'
          style={{
            width: '30px',
            height: '35px',
            borderRadius: '50%',
            objectFit: 'cover',
            display: 'block'
          }}
          onError={() => {
            return (
              <Svg
                width='30'
                height='35'
                viewBox='0 0 24 28'
              >
                {profileAvatarSVG}
              </Svg>
            );
          }}
        />
      );
    }
    return (
      <Svg
        width='30'
        height='35'
        viewBox='0 0 24 28'
      >
        {profileAvatarSVG}
      </Svg>
    );
  };

  const renderProfileImageForEditModal = () => {
    if (people?.image_url) {
      const imageUrl = people?.image_url?.includes('https')
        ? people?.image_url
        : `${config.bucketPath}${people?.image_url}`;

      return (
        <img
          src={imageUrl}
          alt='Profile'
          style={{
            width: '60px',
            height: '65px',
            borderRadius: '50%',
            objectFit: 'cover',
            display: 'block'
          }}
          onError={() => {
            return (
              <Svg
                width='60'
                height='65'
                viewBox='0 0 24 28'
              >
                {profileAvatarSVG}
              </Svg>
            );
          }}
        />
      );
    }
    return (
      <Svg
        width='60'
        height='65'
        viewBox='0 0 24 28'
      >
        {profileAvatarSVG}
      </Svg>
    );
  };

  return (
    <>
      <HStack
        p={'10px'}
        pr={'35px'}
        w={'100%'}
        h={'100%'}
        justifyContent={'space-between'}
      >
        <VStack
          // p={"10px"}
          w={'100%'}
          // h={"100%"}
          justifyContent={'center'}
        >
          <HStack
            space={3}
            alignItems={'center'}
            mb={hasPermission('UPLOAD_INDIVIDUAL_DOCS') && 2}
            w={'100%'}
            justifyContent={'space-between'}
          >
            <Box
              flexDirection='row'
              justifyContent={'space-between'}
              alignItems='center'
              gap='1rem'
            >
              <HStack
                alignItems='center'
                space={4}
              >
                {renderProfileImage()}
                {people?.display_name && people?.display_name !== 'nan' && (
                  <Tooltip label={people?.display_name}>
                    <Text
                      color={COLORS.ATTACHMENTS_TEXT_COLOR}
                      fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                      fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                      pb={'4px'}
                      wordBreak='break-all'
                      maxWidth='200px'
                      overflow='hidden'
                      textOverflow='ellipsis'
                      whiteSpace='nowrap'
                    >
                      {people?.display_name}
                    </Text>
                  </Tooltip>
                )}
              </HStack>
            </Box>
            {(hasPermission('RAISE_REQUEST') || hasPermission('VENDOR')) && !shouldHideEditDeleteIcons &&
              <Box
                flexDirection='row'
                justifyContent={'space-between'}
                alignItems='center'
                gap='1rem'
              >
                <Pressable
                  onPress={() => handleEditClick(people)}
                  // disabled={isDeleteDisabled}
                  // opacity={isDeleteDisabled ? 0.5 : 1}
                  // pointerEvents={isDeleteDisabled ? 'none' : 'auto'}
                >
                  <Avatar
                    bg={'none'}
                    size={'sm'}
                  >
                    {editIconDetailsPage}
                  </Avatar>
                </Pressable>

                <Pressable
                  onPress={() => handleDeleteClick(people)}
                  // disabled={isDeleteDisabled}
                  // opacity={isDeleteDisabled ? 0.5 : 1}
                  // pointerEvents={isDeleteDisabled ? 'none' : 'auto'}
                >
                  <Avatar
                    bg={'none'}
                    size={'xs'}
                  >
                    {deleteCardsIconSVG}
                  </Avatar>
                </Pressable>

              </Box>
            }
          </HStack>

          <Box
            flexDirection='row'
            alignItems='center'
            gap='1rem'
          >
            {people &&
              people?.company_name &&
              people?.company_name !== 'nan' && (
                <Text
                  color={COLORS.ATTACHMENTS_TEXT_COLOR}
                  fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                  fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                  wordBreak='break-all'
                >
                  {people.company_name}
                </Text>
              )}
            <CommonBadge
              text={people.tag}
              colorScheme={COLORS.ATTENDEES_TYPE_COLOR}
              closebtn={false}
              textColor={COLORS.ATTACHMENTS_TEXT_COLOR}
              fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE}
              fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
            />
            {people?.category && (
              <CommonBadge
                text={capitalizeFirst(people?.category)}
                colorScheme={COLORS.ATTENDEES_TYPE_COLOR}
                closebtn={false}
                textColor={COLORS.ATTACHMENTS_TEXT_COLOR}
                fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE}
                fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
              />
            )}
          </Box>
          <HStack
            flexDirection='row'
            alignItems='start'
            py={2}
          >
            <Text
              color={COLORS.ATTACHMENTS_TEXT_COLOR}
              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
              fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
              wordBreak='break-all'
            >
              About :{' '}
            </Text>
            {people?.about ? (
              <Tooltip label={people?.about}>
                <Text
                  color={COLORS.ATTACHMENTS_TEXT_COLOR}
                  fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                  fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                  wordBreak='break-all'
                  maxWidth='250px'
                  overflow='hidden'
                  textOverflow='ellipsis'
                  whiteSpace='nowrap'
                >
                  {people?.about}
                </Text>
              </Tooltip>
            ) : (
              <Text
                color={COLORS.ATTACHMENTS_TEXT_COLOR}
                fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                wordBreak='break-all'
              >
                N/A
              </Text>
            )}
          </HStack>
          <HStack w='100%'>
            <HStack w='100%'>
              <Text
                color={COLORS.ATTACHMENTS_TEXT_COLOR}
                fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE}
                fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
                wordBreak='break-all'
              >
                {people.email}
              </Text>
              {people?.phone_code && people?.phone_number && (
                <>
                  <DividerLine height='auto' />
                  <Text
                    color={COLORS.ATTACHMENTS_TEXT_COLOR}
                    fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE}
                    fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
                    wordBreak='break-all'
                  >
                    {formatPhoneNumber(people.phone_code, people.phone_number)}
                  </Text>
                </>
              )}
              {hasPermission('VENDOR') &&
                people?.boarding_pass_path?.length > 0 && (
                  <>
                    <DividerLine height='auto' />
                    <DocumentList people={people} />
                  </>
                )}
            </HStack>
            {hasPermission('UPLOAD_INDIVIDUAL_DOCS') &&
              statusMap.indexOf(status) <= 1 && (
                <PopOverModal
                  isOpen={isOpen}
                  onClose={onClose}
                  onOpenPress={() => {
                    handleUploadDoc(people, 'people');
                    onOpenPress();
                  }}
                  body={popOverBody()}
                  boxShadow='none'
                />
              )}
          </HStack>
        </VStack>
        {((hasPermission('UPLOAD_INDIVIDUAL_DOCS') &&
          statusMap.indexOf(status) <= 2 &&
          !hasPermission('VENDOR')) ||
          (selected &&
            statusMap.indexOf(status) <= 2 &&
            hasPermission('VENDOR'))) &&
          total <= 4 && (
            <Tooltip label='Upload Documents'>
              <Pressable
                alignItems={'center'}
                justifyContent={'center'}
                onPress={() => {
                  handleUploadDoc(people, 'people');
                  setUploadActiveButton('upload_docs');
                  openModal();
                }}
              >
                {uploadIconSVG}
              </Pressable>
            </Tooltip>
          )}
      </HStack>

      {/* Edit Modal */}
      {showEditModal && !MobileWindow ? (
        <CustomModal
          size='md'
          top={'150px'}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          header={getEditModalHeader()}
          body={getEditModalBody()}
          footer={getEditModalFooter()}
          width='600px'
          height='fit-content'
        />
      ) : (
        <MobileModelSheet
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          header={getEditModalHeader()}
          body={getEditModalBody()}
          footer={getEditModalFooter()}
          maxH={'90vh'}
          position='hidden'
        />
      )}

      {/* Delete Modal */}
      {showDeleteModal && !MobileWindow ? (
        <CustomModal
          size='md'
          top={'200px'}
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          header={getDeleteModalHeader()}
          body={getDeleteModalBody()}
          footer={getDeleteModalFooter()}
          width='fit-content'
          height='fit-content'
        />
      ) : (
        <MobileModelSheet
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          header={getDeleteModalHeader()}
          body={getDeleteModalBody()}
          footer={getDeleteModalFooter()}
          maxH={'80vh'}
          position='hidden'
        />
      )}
    </>
  );
};

export default PeopleDetails;
